import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams, useLocation } from 'react-router-dom';
import { useAuthStore } from './stores';
import { AppLayout } from './components/layout';

// Pages
import { DashboardPage } from './pages/DashboardPage';
import { CalendarPage } from './pages/CalendarPage';
import { RoutePlannerPage } from './pages/RoutePlannerPage';
import { SettingsPage } from './pages/SettingsPage';
import { LoginPage } from './pages/LoginPage';
import { NieuweAanvraagPage } from './pages/NieuweAanvraagPage';
import { WebshopPage } from './pages/WebshopPage';
import { NaleveringPage } from './pages/NaleveringPage';
import { SetPasswordPage } from './pages/SetPasswordPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { UsersPage } from './pages/UsersPage';
import { KioskPage } from './pages/KioskPage';
import { PersoneelPage } from './pages/PersoneelPage';
import { StaffDetailPage } from './pages/StaffDetailPage';
import { UrenPage } from './pages/UrenPage';
import { KasPage } from './pages/KasPage';
import { InkoopPage } from './pages/InkoopPage';
import { DeclaratiesPage } from './pages/DeclaratiesPage';
import { ControlePage } from './pages/ControlePage';
import { MarketingDashboardPage } from './pages/marketing/MarketingDashboardPage';
import { MarketingCampagnesPage } from './pages/marketing/MarketingCampagnesPage';
import { CampagneDetailPage } from './pages/marketing/CampagneDetailPage';
import { MarketingJourneysPage } from './pages/marketing/MarketingJourneysPage';
import { JourneyDetailPage } from './pages/marketing/JourneyDetailPage';
import { MarketingSegmentenPage } from './pages/marketing/MarketingSegmentenPage';
import { MarketingContactenPage } from './pages/marketing/MarketingContactenPage';
import { MarketingInstellingenPage } from './pages/marketing/MarketingInstellingenPage';
import { SeoContentPage } from './pages/marketing/SeoContentPage';
import { MarketingSocialPage } from './pages/marketing/MarketingSocialPage';
import { MarketingSocialKeurenPage } from './pages/marketing/MarketingSocialKeurenPage';
import { MarketingGbpPage } from './pages/marketing/MarketingGbpPage';
import { BeeldbibliotheekPage } from './pages/marketing/BeeldbibliotheekPage';
import { AutomatiseringPage } from './pages/AutomatiseringPage';
import { TelefoonPage } from './pages/TelefoonPage';
import { AgentVerbeteringenPage } from './pages/AgentVerbeteringenPage';
import { SignalenPage } from './pages/SignalenPage';
import { WachtlijstPage } from './pages/WachtlijstPage';
import { LogboekPage } from './pages/LogboekPage';
import { TelefoonInstellingenPage } from './pages/TelefoonInstellingenPage';
import { GebiedenPage } from './pages/GebiedenPage';
import { AdsPage } from './pages/AdsPage';
import { FinancieelPage } from './pages/FinancieelPage';
import { CijfersPage } from './pages/CijfersPage';
import { PartnersPage } from './pages/PartnersPage';
import { PlatformPage } from './pages/PlatformPage';
import { KennisbankPage } from './pages/KennisbankPage';
import { KennisbankZoekPage } from './pages/KennisbankZoekPage';
import { PrijzenPage } from './pages/PrijzenPage';
import { WasserijPage } from './pages/WasserijPage';
import { RoosterPage } from './pages/RoosterPage';
import { VerlofPage } from './pages/VerlofPage';
import { BerichtenPage } from './pages/BerichtenPage';
import { InboxV3Page } from './pages/InboxV3Page';
import { KlantenPage } from './pages/KlantenPage';
import { KlantDetailPage } from './pages/KlantDetailPage';
import { AbonnementenPage } from './pages/AbonnementenPage';
import { OrderDetailPage } from './pages/OrderDetailPage';
import { OrdersPage } from './pages/OrdersPage';
import { ReparatiesPage } from './pages/ReparatiesPage';
import { ReparateursPage } from './pages/ReparateursPage';
import { RisicoInboxPage } from './pages/RisicoInboxPage';
import { ItemsPage } from './pages/ItemsPage';
import { ItemDetailPage } from './pages/ItemDetailPage';
import { InvoiceDetailPage } from './pages/InvoiceDetailPage';
import { FacturenPage } from './pages/FacturenPage';
import { OffertesPage } from './pages/OffertesPage';
import { OfferteDetailPage } from './pages/OfferteDetailPage';
import { OpportunitiesPage } from './pages/OpportunitiesPage';
import { OpportunityDetailPage } from './pages/OpportunityDetailPage';
import { AdminGuard, RoleGuard } from './components/layout';
import { PhoneProvider } from './contexts/PhoneProvider';

// Klachten-pages worden lazy geladen — alleen office-medewerkers met
// klacht-rol bezoeken deze, dus uit het initial bundle houden.
const KlachtenPage = lazy(() =>
  import('./pages/KlachtenPage').then((m) => ({ default: m.KlachtenPage }))
);
const KlachtDetailPage = lazy(() =>
  import('./pages/KlachtDetailPage').then((m) => ({ default: m.KlachtDetailPage }))
);

// Globale klacht-dialogs worden gemount binnen ProtectedRoute zodat ze vanaf
// elke pagina open kunnen via `useKlachtDialogStore`.
import { KlachtRegisterDialog } from './components/klachten/KlachtRegisterDialog';
import { KlachtStatusDialog } from './components/klachten/KlachtStatusDialog';
import { KlachtCloseDialog } from './components/klachten/KlachtCloseDialog';
import { AddRefundDialog } from './components/klachten/actions/AddRefundDialog';
import { AddNewWerkOrderDialog } from './components/klachten/actions/AddNewWerkOrderDialog';
import { AddInvoiceAdjustDialog } from './components/klachten/actions/AddInvoiceAdjustDialog';
import { AddCoulanceVoucherDialog } from './components/klachten/actions/AddCoulanceVoucherDialog';
import { AddNoActionDialog } from './components/klachten/actions/AddNoActionDialog';

// Loading spinner component
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-muted">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin" />
        <p className="text-muted">Laden...</p>
      </div>
    </div>
  );
}

// Protected route wrapper — redirects to /login when no session.
// Wraps authenticated routes in PhoneProvider so the floating softphone and
// click-to-dial buttons are available everywhere.
function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <PhoneProvider>
      <Outlet />
      {/* Globale klacht-dialogs — luisteren naar useKlachtDialogStore en
          tonen zichzelf wanneer hun openDialog-type matcht. */}
      <KlachtRegisterDialog />
      <KlachtStatusDialog />
      <KlachtCloseDialog />
      <AddRefundDialog />
      <AddNewWerkOrderDialog />
      <AddInvoiceAdjustDialog />
      <AddCoulanceVoucherDialog />
      <AddNoActionDialog />
    </PhoneProvider>
  );
}

// Backward-compat: oude `/inbox/:id` URLs uit klantdetail-views, BerichtenTab en
// e-mail-deeplinks worden geredirect naar de split-screen `/inbox?thread=:id`.
function InboxThreadRedirect() {
  const { conversationId } = useParams();
  if (!conversationId) return <Navigate to="/inbox" replace />;
  return <Navigate to={`/inbox?thread=${conversationId}`} replace />;
}

/** /inbox3 (proefroute 14-09) → /inbox met dezelfde query. */
function InboxProefRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/inbox${search}`} replace />;
}

// Guest-only route — redirects authenticated users to home
function GuestRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const validateToken = useAuthStore((s) => s.validateToken);

  // On app mount, explicitly check for existing Supabase session
  useEffect(() => {
    validateToken();
  }, [validateToken]);

  return (
    <BrowserRouter>
      <Routes>
        {/* Auth routes — guest only */}
        <Route path="/login" element={
          <GuestRoute>
            <LoginPage />
          </GuestRoute>
        } />
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

        {/* Kiosk — public, PIN-based auth, no sidebar */}
        <Route path="/kiosk" element={<KioskPage />} />

        {/* Protected routes — AppLayout is een layout-route: sidebar + softphone
            mounten één keer en blijven staan bij navigatie, alleen de Outlet-
            content wisselt. Pagina's renderen GEEN eigen AppLayout meer. */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
          <Route path="/" element={<DashboardPage />} />
          {/* Tijdelijke medewerkersmodus; V2 blijft apart beschikbaar. */}
          {/* Routewissel 14-09 (plan 2026-09-14-inbox-v3): /inbox is V3 (Gmail-indeling
              + kaarten). /inbox2 (V2) is weg; /inbox3 was de proefroute en stuurt door,
              met behoud van ?thread= en ?open=. InboxPage/InboxV2Page blijven bestaan
              tot de snoei na Thailand. */}
          <Route path="/inbox" element={<InboxV3Page />} />
          <Route path="/inbox3" element={<InboxProefRedirect />} />
          <Route path="/inbox/:conversationId" element={<InboxThreadRedirect />} />
          <Route path="/klanten" element={<RoleGuard allow={['admin', 'medewerker']}><KlantenPage /></RoleGuard>} />
          <Route path="/prijzen" element={<RoleGuard allow={['admin', 'medewerker', 'callcenter']}><PrijzenPage /></RoleGuard>} />
          <Route path="/klanten/:customerId" element={<RoleGuard allow={['admin', 'medewerker']}><KlantDetailPage /></RoleGuard>} />
          <Route path="/abonnementen" element={<RoleGuard allow={['admin', 'medewerker']}><AbonnementenPage /></RoleGuard>} />
          <Route path="/orders" element={<RoleGuard allow={['admin', 'medewerker']}><OrdersPage /></RoleGuard>} />
          <Route path="/orders/:id" element={<RoleGuard allow={['admin', 'medewerker']}><OrderDetailPage /></RoleGuard>} />
          <Route path="/items" element={<RoleGuard allow={['admin', 'medewerker']}><ItemsPage /></RoleGuard>} />
          <Route path="/items/:id" element={<RoleGuard allow={['admin', 'medewerker']}><ItemDetailPage /></RoleGuard>} />
          <Route path="/facturen" element={<RoleGuard allow={['admin', 'medewerker']}><FacturenPage /></RoleGuard>} />
          <Route path="/facturen/:id" element={<RoleGuard allow={['admin', 'medewerker']}><InvoiceDetailPage /></RoleGuard>} />
          <Route path="/opportunities" element={<AdminGuard><OpportunitiesPage /></AdminGuard>} />
          <Route path="/opportunities/:id" element={<AdminGuard><OpportunityDetailPage /></AdminGuard>} />
          <Route path="/offertes" element={<RoleGuard allow={['admin', 'medewerker']}><OffertesPage /></RoleGuard>} />
          <Route path="/offertes/:id" element={<RoleGuard allow={['admin', 'medewerker']}><OfferteDetailPage /></RoleGuard>} />
          <Route path="/berichten" element={<BerichtenPage />} />
          <Route path="/telefoon" element={<TelefoonPage />} />
          <Route path="/agent-verbeteringen" element={<AgentVerbeteringenPage />} />
          <Route path="/signalen" element={<RoleGuard allow={['admin', 'medewerker']}><SignalenPage /></RoleGuard>} />
          <Route path="/wachtlijst" element={<RoleGuard allow={['admin', 'medewerker']}><WachtlijstPage /></RoleGuard>} />
          <Route path="/logboek" element={<RoleGuard allow={['admin', 'medewerker']}><LogboekPage /></RoleGuard>} />
          <Route path="/telefoon-instellingen" element={<AdminGuard><TelefoonInstellingenPage /></AdminGuard>} />
          <Route path="/gebieden" element={<AdminGuard><GebiedenPage /></AdminGuard>} />
          <Route path="/nieuwe-aanvraag" element={<NieuweAanvraagPage />} />
          <Route path="/webshop" element={<WebshopPage />} />
          <Route path="/nalevering" element={<NaleveringPage />} />
          <Route path="/kalender" element={<CalendarPage />} />
          <Route path="/route-planner" element={<RoutePlannerPage />} />
          <Route path="/wasserij" element={<WasserijPage />} />
          <Route path="/reparaties" element={<RoleGuard allow={['admin', 'medewerker']}><ReparatiesPage /></RoleGuard>} />
          <Route path="/reparateurs" element={<RoleGuard allow={['admin', 'medewerker']}><ReparateursPage /></RoleGuard>} />
          <Route path="/risicos" element={<RoleGuard allow={['admin', 'medewerker']}><RisicoInboxPage /></RoleGuard>} />
          <Route
            path="/klachten"
            element={
              <RoleGuard allow={['admin', 'medewerker']}>
                <Suspense fallback={<LoadingScreen />}>
                  <KlachtenPage />
                </Suspense>
              </RoleGuard>
            }
          />
          <Route
            path="/klachten/:id"
            element={
              <RoleGuard allow={['admin', 'medewerker']}>
                <Suspense fallback={<LoadingScreen />}>
                  <KlachtDetailPage />
                </Suspense>
              </RoleGuard>
            }
          />
          <Route path="/rooster" element={<RoosterPage />} />
          <Route path="/verlof" element={<VerlofPage />} />
          <Route path="/declaraties" element={<DeclaratiesPage />} />
          <Route path="/kas" element={<KasPage />} />
          <Route path="/inkoop" element={<InkoopPage />} />
          <Route path="/personeel" element={<AdminGuard><PersoneelPage /></AdminGuard>} />
          <Route path="/personeel/:id" element={<AdminGuard><StaffDetailPage /></AdminGuard>} />
          <Route path="/uren" element={<AdminGuard><UrenPage /></AdminGuard>} />
          <Route path="/controle" element={<AdminGuard><ControlePage /></AdminGuard>} />
          <Route path="/financieel" element={<AdminGuard><FinancieelPage /></AdminGuard>} />
          <Route path="/cijfers" element={<AdminGuard><CijfersPage /></AdminGuard>} />
          <Route path="/partners" element={<AdminGuard><PartnersPage /></AdminGuard>} />
          <Route path="/platform" element={<AdminGuard><PlatformPage /></AdminGuard>} />
          <Route path="/kennis" element={<RoleGuard allow={['admin', 'medewerker', 'callcenter']}><KennisbankZoekPage /></RoleGuard>} />
          <Route path="/kennisbank" element={<AdminGuard><KennisbankPage /></AdminGuard>} />
          <Route path="/marketing" element={<AdminGuard><MarketingDashboardPage /></AdminGuard>} />
          <Route path="/marketing/campagnes" element={<AdminGuard><MarketingCampagnesPage /></AdminGuard>} />
          <Route path="/marketing/campagnes/:id" element={<AdminGuard><CampagneDetailPage /></AdminGuard>} />
          <Route path="/marketing/journeys" element={<AdminGuard><MarketingJourneysPage /></AdminGuard>} />
          <Route path="/marketing/journeys/:id" element={<AdminGuard><JourneyDetailPage /></AdminGuard>} />
          <Route path="/marketing/segmenten" element={<AdminGuard><MarketingSegmentenPage /></AdminGuard>} />
          <Route path="/marketing/contacten" element={<AdminGuard><MarketingContactenPage /></AdminGuard>} />
          <Route path="/marketing/instellingen" element={<AdminGuard><MarketingInstellingenPage /></AdminGuard>} />
          <Route path="/marketing/seo-content" element={<AdminGuard><SeoContentPage /></AdminGuard>} />
          <Route path="/marketing/social" element={<RoleGuard allow={['admin', 'medewerker']}><MarketingSocialPage /></RoleGuard>} />
          <Route path="/marketing/social/keuren" element={<RoleGuard allow={['admin', 'medewerker']}><MarketingSocialKeurenPage /></RoleGuard>} />
          <Route path="/marketing/gbp" element={<AdminGuard><MarketingGbpPage /></AdminGuard>} />
          <Route path="/marketing/beeldbibliotheek" element={<AdminGuard><BeeldbibliotheekPage /></AdminGuard>} />
          <Route path="/automatisering" element={<AdminGuard><AutomatiseringPage /></AdminGuard>} />
          {/* Oude campagnes-route → nieuwe module (CampagnesPage zelf verdwijnt bij cutover, Fase 6) */}
          <Route path="/campagnes" element={<Navigate to="/marketing/campagnes" replace />} />
          <Route path="/ads" element={<AdminGuard><AdsPage /></AdminGuard>} />
          <Route path="/instellingen" element={<SettingsPage />} />
          <Route path="/gebruikers" element={<AdminGuard><UsersPage /></AdminGuard>} />
          </Route>
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
